import React, { useEffect, useRef, useState, useCallback } from 'react';
import './LogsOverlay.css';
import GraphOverlay from './GraphOverlay';

export default function LogsOverlay({ onClose }) {
  // Data
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);

  // UI Filters
  const [usernameFilter, setUsernameFilter] = useState('');
  const [dayFilter, setDayFilter] = useState(''); // YYYY-MM-DD
  const [showErrors, setShowErrors] = useState(true);
  const [showLogs, setShowLogs] = useState(true);
  const [showInfo, setShowInfo] = useState(true);

  // Graph overlay toggle
  const [showGraphOverlay, setShowGraphOverlay] = useState(false);

  // Pagination (virtual, for MATCHED rows)
  const LIMIT = 200;
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // Refs & guards
  const listRef = useRef(null);
  const didInitRef = useRef(false);
  const inFlightRef = useRef(false);

  // “Server cursor”: where the next server fetch should read from
  const serverOffsetRef = useRef(0);
  const lastReqKeyRef = useRef('');
  const nearBottomArmedRef = useRef(true);

  // Active filters (immutable snapshot used during a fetch round)
  const activeFiltersRef = useRef({ username: '', date: '', types: ['error','log','info'] });

  // Build type list
  const currentTypes = () => {
    const t = [];
    if (showErrors) t.push('error');
    if (showLogs) t.push('log');
    if (showInfo) t.push('info');
    return t.length ? t : ['error','log','info']; // never empty
  };

  // Client-side predicate (works with your DB schema: date, time, user/username, message/msg)
  const matchesFilters = (row, f) => {
    const username = (row.user || row.username || '').toLowerCase();
    const message  = (row.msg || row.message || '');

    // type
    const isError = message.startsWith('[ERROR]');
    const isLog   = message.startsWith('[LOG]');
    const isInfo  = !isError && !isLog;
    const typeOk =
      (isError && f.types.includes('error')) ||
      (isLog   && f.types.includes('log'))   ||
      (isInfo  && f.types.includes('info'));

    // username
    const userOk = !f.username || username.includes(f.username.toLowerCase());

    // date (exact match on YYYY-MM-DD)
    const rowDate = row.date || '';
    const dateOk  = !f.date || rowDate === f.date;

    return typeOk && userOk && dateOk;
  };

  // Build URL with BOTH `date` and `day` (compat), plus username & types
  const makeQuery = (offset, f) => {
    const params = new URLSearchParams();
    params.set('limit', String(LIMIT));
    params.set('offset', String(offset));
    if (f.username) params.set('username', f.username);
    if (f.date) { params.set('date', f.date); params.set('day', f.date); } // send both
    if (f.types?.length) params.set('types', f.types.join(','));
    return `/get-logs/?${params.toString()}`;
  };

  // Progressive fetch: keep requesting pages until we’ve collected up to LIMIT matching rows
  const fetchPageWithClientFilter = useCallback(async (initial = false) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);

    try {
      const f = activeFiltersRef.current;
      const reqKey = `${serverOffsetRef.current}|${f.username}|${f.date}|${f.types.join('-')}`;
      if (!initial && reqKey === lastReqKeyRef.current) {
        return; // same request key already in flight/done
      }
      lastReqKeyRef.current = reqKey;

      // Where matched rows will be accumulated (for this round)
      const roundMatches = [];

      // Keep pulling from server until we gather LIMIT matches or server is exhausted
      let keepGoing = true;
      let safety = 20; // cap to avoid infinite loops if server misbehaves

      while (keepGoing && safety-- > 0) {
        const url = makeQuery(serverOffsetRef.current, f);
        const res = await fetch(url);
        const data = await res.json();

        const batch = Array.isArray(data.logs) ? data.logs : [];
        const serverHasMore =
          typeof data.has_more !== 'undefined'
            ? Boolean(data.has_more)
            : (batch.length === LIMIT);

        // Advance server cursor
        if (typeof data.next_offset === 'number') {
          serverOffsetRef.current = data.next_offset;
        } else {
          serverOffsetRef.current += batch.length;
        }

        // If backend didn’t filter, client filter will enforce it
        const matched = f ? batch.filter((row) => matchesFilters(row, f)) : batch;
        roundMatches.push(...matched);

        // Stop if we reached LIMIT matches for this round, or server has no more
        if (roundMatches.length >= LIMIT || !serverHasMore) {
          keepGoing = false;
          // Update global hasMore based on SERVER’s remaining data (not matches)
          setHasMore(serverHasMore);
        }
      }

      if (initial) {
        // Fresh filtered view
        setLogs(roundMatches);
      } else {
        // Append additional matched rows
        setLogs((prev) => [...prev, ...roundMatches]);
      }
    } catch (e) {
      console.error('Failed to fetch logs', e);
      setHasMore(false);
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, []);

  // Keep filteredLogs in sync (server+client-filtered already)
  useEffect(() => { setFilteredLogs(logs); }, [logs]);

  // First load: no filters (all)
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    activeFiltersRef.current = { username: '', date: '', types: ['error','log','info'] };
    serverOffsetRef.current = 0;
    fetchPageWithClientFilter(true);
  }, [fetchPageWithClientFilter]);

  // Infinite scroll (uses progressive fetch)
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => {
      if (inFlightRef.current || loading) return;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
      if (!nearBottom) return;
      if (!nearBottomArmedRef.current) return;

      nearBottomArmedRef.current = false;
      fetchPageWithClientFilter(false);
      setTimeout(() => { nearBottomArmedRef.current = true; }, 800);
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [fetchPageWithClientFilter, loading]);

  // Apply filters → reset cursors and fetch first filtered page
  const applyFilters = () => {
    activeFiltersRef.current = {
      username: usernameFilter.trim(),
      date: dayFilter.trim(),          // exact YYYY-MM-DD
      types: currentTypes(),
    };

    // Reset everything
    setLogs([]);
    setHasMore(true);
    serverOffsetRef.current = 0;
    lastReqKeyRef.current = '';
    inFlightRef.current = false;

    // Fetch first page that MATCHES (client-enforced)
    fetchPageWithClientFilter(true);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 9999
    }}>
      <div style={{
        position: 'relative',
        backgroundColor: '#f3f3f3',
        padding: '20px',
        borderRadius: '10px',
        width: '80%',
        maxHeight: '80%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          padding: '5px 10px',
          backgroundColor: '#333',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px'
        }}>
          Close
        </button>

        <h2>User Logs</h2>

        {/* Filter Row */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          margin: '10px 0',
          justifyContent: 'center',
          width: '100%'
        }}>
          <input
            type="text"
            placeholder="User Name"
            value={usernameFilter}
            onChange={(e) => setUsernameFilter(e.target.value)}
            style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
          <input
            type="text"
            placeholder="Day (YYYY-MM-DD)"
            value={dayFilter}
            onChange={(e) => setDayFilter(e.target.value)}
            style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
          <label>
            <input
              type="checkbox"
              checked={showErrors}
              onChange={() => setShowErrors(!showErrors)}
              style={{ marginRight: '5px' }}
            />
            Errors
          </label>
          <label>
            <input
              type="checkbox"
              checked={showLogs}
              onChange={() => setShowLogs(!showLogs)}
              style={{ marginRight: '5px' }}
            />
            Logs
          </label>
          <label>
            <input
              type="checkbox"
              checked={showInfo}
              onChange={() => setShowInfo(!showInfo)}
              style={{ marginRight: '5px' }}
            />
            Info
          </label>

          <button
            onClick={applyFilters}
            style={{
              padding: '6px 12px',
              backgroundColor: '#007BFF',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            title="Fetch first page that matches filters (client enforced if needed)"
          >
            Apply Filters
          </button>

          <button
            onClick={() => setShowGraphOverlay(true)}
            style={{
              padding: '6px 12px',
              backgroundColor: '#ff8c00',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            title="Open logs graph overlay"
          >
            Analytics
          </button>
        </div>

        {/* Logs Output + Infinite Scroll */}
        <div
          ref={listRef}
          style={{
            backgroundColor: '#eaeaea',
            padding: '10px',
            width: '100%',
            borderRadius: '5px',
            marginTop: '10px',
            overflowY: 'auto',
            maxHeight: '60vh'
          }}
        >
          {filteredLogs.map((log, idx) => (
            <div key={`${log.date}-${log.time}-${idx}`} style={{ fontFamily: 'monospace', margin: '4px 0' }}>
              [{log.date} {log.time}] <strong>{log.user || log.username || 'Unknown'}</strong>: {log.msg || log.message}
            </div>
          ))}

          {/* Manual “Load more” */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
            {hasMore ? (
              <button
                onClick={() => fetchPageWithClientFilter(false)}
                disabled={loading || inFlightRef.current}
                style={{
                  padding: '6px 12px',
                  backgroundColor: (loading || inFlightRef.current) ? '#999' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (loading || inFlightRef.current) ? 'default' : 'pointer'
                }}
              >
                {loading ? 'Loading…' : 'Load more logs'}
              </button>
            ) : (
              <span style={{ color: '#555', fontSize: 12 }}>No more logs</span>
            )}
          </div>
        </div>

        {showGraphOverlay && (
          <GraphOverlay
            logs={filteredLogs}
            onClose={() => setShowGraphOverlay(false)}
          />
        )}
      </div>
    </div>
  );
}
